import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Brain, Target, Sparkles, Wallet, Heart, Briefcase, 
  GraduationCap, Home, ArrowRight, ArrowLeft, Plus, 
  Trash2, Bot, FileText, Milestone, CheckCircle, Edit
} from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useOnboarding } from "@/contexts/OnboardingContext";
import { mastermindSpacesService } from "@/services/mastermindSpacesService";
import type { Source } from "@shared/schema";

const iconOptions = [
  { name: "brain", icon: Brain, color: "text-blue-500" },
  { name: "target", icon: Target, color: "text-green-500" },
  { name: "sparkles", icon: Sparkles, color: "text-purple-500" },
  { name: "wallet", icon: Wallet, color: "text-emerald-500" },
  { name: "heart", icon: Heart, color: "text-pink-500" },
  { name: "briefcase", icon: Briefcase, color: "text-orange-500" },
  { name: "graduation", icon: GraduationCap, color: "text-indigo-500" },
  { name: "home", icon: Home, color: "text-amber-500" },
];

interface SpaceWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources?: Source[];
}

export function SpaceWizard({ open, onOpenChange, sources = [] }: SpaceWizardProps) {
  const [step, setStep] = useState(1);
  const { toast } = useToast();
  const { completeMilestone, hasMilestone } = useOnboarding();

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetWizard();
    }
    onOpenChange(newOpen);
  };

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("brain");
  const [goals, setGoals] = useState<string[]>([]);
  const [goalInput, setGoalInput] = useState("");
  const [agents, setAgents] = useState<Array<{ id: string; name: string; role: string; instructions: string }>>([]);
  const [agentInput, setAgentInput] = useState({ name: "", role: "", instructions: "" });
  const [linkedSourceIds, setLinkedSourceIds] = useState<string[]>([]);
  const [milestones, setMilestones] = useState<Array<{ id: string; title: string; description: string; completed: boolean; progress: number }>>([]);
  const [milestoneInput, setMilestoneInput] = useState({ title: "", description: "" });

  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;

  const createSpaceMutation = useMutation({
    mutationFn: async (data: any) => {
      return await mastermindSpacesService.createMastermindSpace(data);
    },
    onSuccess: () => {
      // Invalidate all spaces queries
      queryClient.invalidateQueries({ queryKey: ["spaces"] }); // For AppSidebar
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] }); // For spaces page
      toast({
        title: "Success",
        description: "Mastermind Space created successfully!",
      });
      // Trigger first_space milestone for new users (unlocks Library)
      if (!hasMilestone('first_space')) {
        completeMilestone('first_space');
      }
      resetWizard();
      onOpenChange(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create space. Please try again.",
        variant: "destructive",
      });
    },
  });

  const resetWizard = () => {
    setStep(1);
    setName("");
    setDescription("");
    setSelectedIcon("brain");
    setGoals([]);
    setGoalInput("");
    setAgents([]);
    setAgentInput({ name: "", role: "", instructions: "" });
    setLinkedSourceIds([]);
    setMilestones([]);
    setMilestoneInput({ title: "", description: "" });
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleAddGoal = () => {
    if (goalInput.trim()) {
      setGoals([...goals, goalInput.trim()]);
      setGoalInput("");
    }
  };

  const handleRemoveGoal = (index: number) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  const handleAddAgent = () => {
    if (agentInput.name.trim() && agentInput.role.trim()) {
      const newAgent = {
        id: `agent-${Date.now()}`,
        name: agentInput.name,
        role: agentInput.role,
        instructions: agentInput.instructions,
        createdAt: new Date().toISOString(),
      };
      setAgents([...agents, newAgent]);
      setAgentInput({ name: "", role: "", instructions: "" });
    }
  };

  const handleRemoveAgent = (id: string) => {
    setAgents(agents.filter(a => a.id !== id));
  };

  const handleToggleSource = (sourceId: string) => {
    if (linkedSourceIds.includes(sourceId)) {
      setLinkedSourceIds(linkedSourceIds.filter(id => id !== sourceId));
    } else {
      setLinkedSourceIds([...linkedSourceIds, sourceId]);
    }
  };

  const handleAddMilestone = () => {
    if (milestoneInput.title.trim()) {
      const newMilestone = {
        id: `milestone-${Date.now()}`,
        title: milestoneInput.title,
        description: milestoneInput.description,
        completed: false,
        progress: 0,
      };
      setMilestones([...milestones, newMilestone]);
      setMilestoneInput({ title: "", description: "" });
    }
  };

  const handleRemoveMilestone = (id: string) => {
    setMilestones(milestones.filter(m => m.id !== id));
  };

  const handleCreate = () => {
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Please provide a name for your space.",
        variant: "destructive",
      });
      return;
    }

    createSpaceMutation.mutate({
      name,
      description,
      icon: selectedIcon,
      goals,
      agents,
      linkedSourceIds,
      milestones,
    });
  };

  const canProceed = () => {
    if (step === 1) return name.trim() !== "";
    return true; // Other steps are optional
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Mastermind Space - Step {step} of {totalSteps}</DialogTitle>
          <DialogDescription>
            Build a comprehensive AI-powered workspace to manage an entire life domain
          </DialogDescription>
        </DialogHeader>

        <Progress value={progress} className="mb-4" />

        <div className="flex-1 overflow-y-auto py-4">
          {step === 1 && (
            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">Space Name *</label>
                <Input
                  placeholder="e.g., Wealth Building Mastermind"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="wizard-input-name"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  placeholder="What is this space for? What life area will it help you manage?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  data-testid="wizard-textarea-description"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Choose an Icon</label>
                <div className="grid grid-cols-4 gap-2">
                  {iconOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.name}
                        onClick={() => setSelectedIcon(option.name)}
                        className={`p-4 rounded-md border-2 transition-all ${
                          selectedIcon === option.name
                            ? "border-primary bg-primary/10"
                            : "border-transparent bg-accent hover-elevate"
                        }`}
                        data-testid={`wizard-icon-${option.name}`}
                      >
                        <Icon className={`h-8 w-8 mx-auto ${option.color}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Define Your Goals</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  What do you want to achieve in this life domain? Add specific, measurable goals.
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a goal..."
                  value={goalInput}
                  onChange={(e) => setGoalInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddGoal())}
                  data-testid="wizard-input-goal"
                />
                <Button onClick={handleAddGoal} data-testid="wizard-button-add-goal">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {goals.length > 0 && (
                <div className="space-y-2">
                  {goals.map((goal, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-md bg-accent"
                      data-testid={`wizard-goal-${index}`}
                    >
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        <span>{goal}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGoal(index)}
                        data-testid={`wizard-button-remove-goal-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Configure AI Agents</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add specialized AI agents to help you manage this domain with expert guidance.
                </p>
              </div>
              <div className="space-y-3">
                <Input
                  placeholder="Agent name (e.g., Financial Analyst, Investment Advisor)..."
                  value={agentInput.name}
                  onChange={(e) => setAgentInput({ ...agentInput, name: e.target.value })}
                  data-testid="wizard-input-agent-name"
                />
                <Input
                  placeholder="Agent role (e.g., Analyze market trends, Recommend investments)..."
                  value={agentInput.role}
                  onChange={(e) => setAgentInput({ ...agentInput, role: e.target.value })}
                  data-testid="wizard-input-agent-role"
                />
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Instructions (optional): What specific tasks should this agent handle?"
                    value={agentInput.instructions}
                    onChange={(e) => setAgentInput({ ...agentInput, instructions: e.target.value })}
                    rows={2}
                    data-testid="wizard-textarea-agent-instructions"
                  />
                  <Button onClick={handleAddAgent} className="shrink-0" data-testid="wizard-button-add-agent">
                    <Plus className="h-4 w-4 mr-2" />
                    Add
                  </Button>
                </div>
              </div>
              {agents.length > 0 && (
                <div className="space-y-2">
                  {agents.map((agent) => (
                    <div
                      key={agent.id}
                      className="flex items-start gap-3 p-4 rounded-md border"
                      data-testid={`wizard-agent-${agent.id}`}
                    >
                      <div className="p-2 rounded-md bg-primary/10 shrink-0">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium">{agent.name}</h4>
                        <p className="text-sm text-muted-foreground mt-0.5">{agent.role}</p>
                        {agent.instructions && (
                          <p className="text-sm text-muted-foreground mt-2 italic">
                            {agent.instructions}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAgent(agent.id)}
                        data-testid={`wizard-button-remove-agent-${agent.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Link Sources (Optional)</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Connect relevant sources from your knowledge library to this space.
                </p>
              </div>
              {sources.length > 0 ? (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {sources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-start gap-3 p-4 rounded-md border cursor-pointer hover-elevate"
                      onClick={() => handleToggleSource(source.id)}
                      data-testid={`wizard-source-${source.id}`}
                    >
                      <div className={`p-2 rounded-md shrink-0 ${
                        linkedSourceIds.includes(source.id)
                          ? "bg-primary text-primary-foreground"
                          : "bg-accent"
                      }`}>
                        {linkedSourceIds.includes(source.id) ? (
                          <CheckCircle className="h-5 w-5" />
                        ) : (
                          <FileText className="h-5 w-5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium">{source.title}</h4>
                        {source.topics && source.topics.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {source.topics.slice(0, 3).map((topic, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {topic}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    No sources in your library yet. You can add sources later.
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Add Milestones (Optional)</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Break down your goals into actionable milestones to track progress.
                </p>
              </div>
              <div className="space-y-2">
                <Input
                  placeholder="Milestone title..."
                  value={milestoneInput.title}
                  onChange={(e) => setMilestoneInput({ ...milestoneInput, title: e.target.value })}
                  data-testid="wizard-input-milestone-title"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Description (optional)..."
                    value={milestoneInput.description}
                    onChange={(e) => setMilestoneInput({ ...milestoneInput, description: e.target.value })}
                    data-testid="wizard-input-milestone-description"
                  />
                  <Button onClick={handleAddMilestone} data-testid="wizard-button-add-milestone">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {milestones.length > 0 && (
                <div className="space-y-2">
                  {milestones.map((milestone) => (
                    <div
                      key={milestone.id}
                      className="flex items-center justify-between p-3 rounded-md bg-accent"
                      data-testid={`wizard-milestone-${milestone.id}`}
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <Milestone className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{milestone.title}</div>
                          {milestone.description && (
                            <div className="text-sm text-muted-foreground">{milestone.description}</div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMilestone(milestone.id)}
                        data-testid={`wizard-button-remove-milestone-${milestone.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">Review & Create</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Review your Mastermind Space configuration before creating it.
                </p>
              </div>
              <div className="space-y-4">
                <div className="p-4 rounded-md border">
                  <div className="flex items-center gap-3 mb-2">
                    {(() => {
                      const option = iconOptions.find(opt => opt.name === selectedIcon);
                      const Icon = option ? option.icon : Brain;
                      const color = option ? option.color : "text-blue-500";
                      return <Icon className={`h-6 w-6 ${color}`} />;
                    })()}
                    <h4 className="font-semibold text-lg">{name}</h4>
                  </div>
                  {description && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 rounded-md border">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Goals</span>
                    </div>
                    <Badge variant="outline">{goals.length}</Badge>
                  </div>
                  <div className="p-4 rounded-md border">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">AI Agents</span>
                    </div>
                    <Badge variant="outline">{agents.length}</Badge>
                  </div>
                  <div className="p-4 rounded-md border">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Linked Sources</span>
                    </div>
                    <Badge variant="outline">{linkedSourceIds.length}</Badge>
                  </div>
                  <div className="p-4 rounded-md border">
                    <div className="flex items-center gap-2 mb-2">
                      <Milestone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">Milestones</span>
                    </div>
                    <Badge variant="outline">{milestones.length}</Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
            data-testid="wizard-button-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="text-sm text-muted-foreground">
            Step {step} of {totalSteps}
          </div>
          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="wizard-button-next"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCreate}
              disabled={!name.trim() || createSpaceMutation.isPending}
              data-testid="wizard-button-create"
            >
              {createSpaceMutation.isPending ? "Creating..." : "Create Space"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
